import React from 'react';
import { Receipt, Plus, Trash2 } from 'lucide-react';

/**
 * Секция формы с расходами
 */
const ExpensesSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Если у пользователя нет расходов или режим не УСН Доходы-Расходы, не показываем эту секцию
  if (!formData.hasExpenses && formData.taxSystem !== 'usn_profit') return null;

  return (
    <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Receipt className="w-5 h-5 mr-2 text-amber-500" />
        Расходы
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Чекбокс для включения расходов */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="hasExpenses"
              checked={formData.hasExpenses}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать расходы {formData.taxSystem === 'usn_profit' ? '(обязательно для УСН Доходы-Расходы)' : '(опционально)'}
            </span>
          </label>
        </div>

        {formData.hasExpenses && (
          <>
            {/* Сумма расходов */}
            <div className="col-span-full">
              <label htmlFor="expenses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Общая сумма расходов
              </label>
              <input
                type="text"
                id="expenses"
                name="expenses"
                value={formData.expenses}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.expenses ? 'input-error' : ''}`}
                placeholder="Введите сумму расходов"
              />
              {errors.expenses && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.expenses}
                </p>
              )}
            </div>

            {/* Информация о расходах для УСН */}
            {formData.taxSystem === 'usn_profit' && (
              <div className="col-span-full">
                <div className="mt-2 bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700 text-sm">
                  <div className="font-medium mb-1">При УСН Доходы-Расходы:</div>
                  <ul className="text-gray-600 dark:text-gray-400 text-xs space-y-1 list-disc pl-4">
                    <li>Учитываются только документально подтвержденные расходы</li>
                    <li>Даже если расходы превышают доходы, налог будет не менее 1% от дохода (минимальный налог)</li>
                    <li>Фиксированные страховые взносы ИП учитываются в составе расходов</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Детализация расходов (опционально) */}
            <div className="col-span-full mt-2">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Детализация расходов (опционально)
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const newExpenseItems = [...formData.expenseItems, { name: '', amount: '' }];
                    handleInputChange({ 
                      target: { 
                        name: 'expenseItems', 
                        value: newExpenseItems 
                      } 
                    });
                  }}
                  className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  <Plus className="w-3 h-3" /> Добавить статью
                </button>
              </div>

              {formData.expenseItems.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-md">
                  {formData.expenseItems.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => {
                          const newItems = [...formData.expenseItems];
                          newItems[index].name = e.target.value;
                          handleInputChange({ 
                            target: { 
                              name: 'expenseItems', 
                              value: newItems 
                            } 
                          });
                        }}
                        placeholder="Название"
                        className="form-input flex-1 text-xs py-1"
                      />
                      <input
                        type="text"
                        value={item.amount}
                        onChange={(e) => {
                          const newItems = [...formData.expenseItems];
                          newItems[index].amount = e.target.value;
                          handleInputChange({ 
                            target: { 
                              name: 'expenseItems', 
                              value: newItems 
                            } 
                          });
                        }}
                        placeholder="Сумма"
                        className="form-input w-24 text-xs py-1"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newItems = formData.expenseItems.filter((_, i) => i !== index);
                          handleInputChange({ 
                            target: { 
                              name: 'expenseItems', 
                              value: newItems 
                            } 
                          });
                        }}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Добавьте статьи расходов для более детального учета
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExpensesSection;